import axios from 'axios'
import { getApiBaseUrl } from '../utils/config.js'

const API_BASE_URL = `${getApiBaseUrl()}/api/admin`
const AUTH_API_BASE_URL = `${getApiBaseUrl()}/api`
const MAIN_API_BASE_URL = `${getApiBaseUrl()}/api/v1`

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Auth API instance for /api/auth/* endpoints (not under /api/admin)
export const authApiInstance = axios.create({
  baseURL: AUTH_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Main API instance for non-admin endpoints (Amenity, ParkingSpot, etc.)
const mainApi = axios.create({
  baseURL: MAIN_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Shared interceptor setup for auth
const setupInterceptors = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('authToken')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      const selectedBuildingId = localStorage.getItem('selectedBuildingId')
      if (selectedBuildingId) {
        config.headers['CurrentBuildingId'] = selectedBuildingId
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true

        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          try {
            const { authService } = await import('./authApi')
            const response = await authService.refreshToken({
              token: localStorage.getItem('authToken'),
              refreshToken: refreshToken
            })

            const { token: newToken, refreshToken: newRefreshToken } = response.data
            localStorage.setItem('authToken', newToken)
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken)
            }

            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return instance(originalRequest)
          } catch {
            localStorage.removeItem('authToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('userProfile')
            window.location.href = '/login'
          }
        } else {
          localStorage.removeItem('authToken')
          localStorage.removeItem('userProfile')
          window.location.href = '/login'
        }
      }

      return Promise.reject(error)
    }
  )
}

setupInterceptors(api)
setupInterceptors(mainApi)

// Auth API only needs the request interceptor (attach token header),
// NOT the 401 response interceptor — login/refresh endpoints naturally
// return 401 on invalid credentials and must not trigger window.location redirect.
authApiInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

export const demoApi = {
  generateDemo: (demoRequest) => api.post('/Demo/generate', demoRequest),
  cleanup: () => api.delete('/Demo/cleanup'),
}

export const buildingApi = {
  test: () => api.get('/Building/test'),
}

export const emailTemplatesApi = {
  getTypes: () => api.get('/email-templates/types'),
  getVariables: () => api.get('/email-templates/variables'),
  getVariablesByType: (templateType) => api.get(`/email-templates/variables/${templateType}`),
  list: () => api.get('/email-templates/list'),
  create: (template) => api.post('/email-templates/create', template),
  get: (templateType) => api.get(`/email-templates/get/${templateType}`),
  update: (template) => api.put('/email-templates/update', template),
  delete: (templateType) => api.delete(`/email-templates/delete/${templateType}`),
  send: (emailRequest) => api.post('/email-templates/send', emailRequest),
  sendBulk: (bulkEmailRequest) => api.post('/email-templates/send-bulk', bulkEmailRequest),
  sendBulkTest: (bulkTestRequest) => api.post('/email-templates/send-bulk-test', bulkTestRequest),
  generateAI: (aiRequest) => api.post('/email-templates/ai/generate', aiRequest, { timeout: 120000 }),
  reviseAI: (data) => api.post('/email-templates/ai/revise', data, { timeout: 120000 }),
  generateTestData: (templateType) => api.post(`/email-templates/ai/generate-test-data/${templateType}`, null, { timeout: 120000 }),
  getBrandingConfig: () => api.get('/email-templates/branding-config'),
  updateBrandingConfig: (data) => api.put('/email-templates/branding-config', data),
  generateReferenceHtml: (data) => api.post('/email-templates/generate-reference-html', data, { timeout: 120000 }),
  getBatchStreamUrl: () => `${API_BASE_URL}/email-templates/batch-regenerate-stream`,
}

export const staticAssetsApi = {
  list: (params = {}) => api.get('/StaticAssets', { params }),
  getCategories: () => api.get('/StaticAssets/categories'),
  upload: (formData) => api.post('/StaticAssets/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/StaticAssets/${id}`),
  deleteMultiple: (ids) => api.post('/StaticAssets/delete-multiple', ids),
}

export const smsTemplatesApi = {
  getTypes: () => api.get('/sms-templates/types'),
  getVariables: () => api.get('/sms-templates/variables'),
  getVariablesByType: (templateType) => api.get(`/sms-templates/variables/${templateType}`),
  list: () => api.get('/sms-templates/list'),
  create: (template) => api.post('/sms-templates/create', template),
  get: (templateType) => api.get(`/sms-templates/get/${templateType}`),
  update: (template) => api.put('/sms-templates/update', template),
  delete: (templateType) => api.delete(`/sms-templates/delete/${templateType}`),
  send: (smsRequest) => api.post('/sms-templates/send', smsRequest),
  sendBulk: (bulkSmsRequest) => api.post('/sms-templates/send-bulk', bulkSmsRequest),
  generateAI: (aiRequest) => api.post('/sms-templates/ai/generate', aiRequest, { timeout: 120000 }),
}

export const announcementApi = {
  // 1. Get all announcements with pagination and advanced filtering
  list: (params = {}) => api.get('/Announcement', { params }),
  
  // 2. Create new announcement
  create: (announcement) => api.post('/Announcement', announcement),
  
  // 3. Get announcement by ID
  get: (announcementId) => api.get(`/Announcement/${announcementId}`),
  
  // 4. Update existing announcement
  update: (announcementId, announcement) => api.put(`/Announcement/${announcementId}`, announcement),
  
  // 5. Delete announcement
  delete: (announcementId, forceDelete = false) => api.delete(`/Announcement/${announcementId}${forceDelete ? '?forceDelete=true' : ''}`),
  
  // 6. Get announcement recipients with pagination
  getRecipients: (announcementId, params = {}) => api.get(`/Announcement/${announcementId}/recipients`, { params }),
  
  // 7. Update recipient status
  updateRecipientStatus: (announcementId, userId, params = {}) => api.put(`/Announcement/${announcementId}/recipients/${userId}`, null, { params }),
  
  // 8. Resend announcement to specific recipient
  resendToRecipient: (announcementId, userId) => api.post(`/Announcement/${announcementId}/recipients/${userId}/resend`),
  
  // 9. Get announcement statistics
  getStats: (announcementId) => api.get(`/Announcement/${announcementId}/stats`),
  
  // 10. Get global announcement statistics
  getGlobalStats: () => api.get('/Announcement/stats'),
  
  // 11. Get building announcement statistics
  getBuildingStats: (buildingId) => api.get(`/Announcement/buildings/${buildingId}/stats`),
  
  // 12. Bulk update announcement status
  bulkUpdateStatus: (bulkData) => api.put('/Announcement/bulk/status', bulkData),
  
  // 13. Bulk delete announcements
  bulkDelete: (bulkData) => api.delete('/Announcement/bulk', { data: bulkData }),
  
  // 14. Export announcements to Excel
  export: (params = {}) => api.get('/Announcement/export', { params, responseType: 'blob' }),

  // 15. Generate announcement content with AI
  generateAI: (aiRequest) => api.post('/Announcement/ai/generate', aiRequest, { timeout: 120000 }),

  // 16. Revise announcement content with AI
  reviseAI: (aiRequest) => api.post('/Announcement/ai/revise', aiRequest, { timeout: 120000 }),
}

export const deliveryApi = {
  // Core CRUD Operations
  
  // 1. Get all deliveries with pagination and advanced filtering
  list: (params = {}) => api.get('/Delivery', { params }),
  
  // 2. Create new delivery
  create: (delivery) => api.post('/Delivery', delivery),
  
  // 3. Get delivery by ID
  get: (deliveryId) => api.get(`/Delivery/${deliveryId}`),
  
  // 4. Update delivery
  update: (deliveryId, delivery) => api.put(`/Delivery/${deliveryId}`, delivery),
  
  // 5. Delete delivery
  delete: (deliveryId, forceDelete = false) => api.delete(`/Delivery/${deliveryId}${forceDelete ? '?forceDelete=true' : ''}`),
  
  // Status Management
  
  // 6. Mark as delivered
  markDelivered: (deliveryId, deliveryDate = null) => {
    const params = deliveryDate ? { deliveryDate } : {}
    return api.post(`/Delivery/${deliveryId}/mark-delivered`, null, { params })
  },
  
  // 7. Mark as collected
  markCollected: (deliveryId, collectedDate = null) => {
    const params = collectedDate ? { collectedDate } : {}
    return api.post(`/Delivery/${deliveryId}/mark-collected`, null, { params })
  },
  
  // 8. Update delivery status
  updateStatus: (deliveryId, statusUpdate) => api.put(`/Delivery/${deliveryId}/status`, statusUpdate),
  
  // Statistics & Analytics
  
  // 9. Get overall delivery statistics
  getStats: (params = {}) => api.get('/Delivery/stats', { params }),
  
  // 10. Get building-specific statistics
  getBuildingStats: (buildingId, params = {}) => api.get(`/Delivery/buildings/${buildingId}/stats`, { params }),
  
  // Specialized Lists
  
  // 11. Get overdue deliveries
  getOverdue: (params = {}) => api.get('/Delivery/overdue', { params }),
  
  // 12. Get pending deliveries
  getPending: (params = {}) => api.get('/Delivery/pending', { params }),
  
  // 13. Get deliveries awaiting collection
  getAwaitingCollection: (params = {}) => api.get('/Delivery/awaiting-collection', { params }),
  
  // Bulk Operations
  
  // 14. Bulk update delivery status
  bulkUpdateStatus: (bulkData) => api.put('/Delivery/bulk/status', bulkData),
  
  // 15. Bulk delete deliveries
  bulkDelete: (bulkData) => api.delete('/Delivery/bulk', { data: bulkData }),
  
  // Utility Operations
  
  // 16. Get delivery recipients
  getRecipients: (deliveryId, params = {}) => api.get(`/Delivery/${deliveryId}/recipients`, { params }),
  
  // 17. Transfer delivery
  transfer: (deliveryId, transferData) => api.post(`/Delivery/${deliveryId}/transfer`, transferData),
  
  // 18. Resend notification
  resendNotification: (deliveryId, notificationData) => api.post(`/Delivery/${deliveryId}/resend-notification`, notificationData),
  
  
  // 20. Send collection reminder
  sendReminder: (deliveryId, reminderData) => api.post(`/Delivery/${deliveryId}/send-reminder`, reminderData),
  
  // 21. Export deliveries
  export: (params = {}) => api.get('/Delivery/export', { params, responseType: 'blob' }),
}

export const enumsApi = {
  // Get all available building module types
  getModuleTypes: () => api.get('/Enums/module-types'),

  // Get all available license types
  getLicenseTypes: () => api.get('/Enums/license-types'),

  // Get all available activation methods
  getActivationMethods: () => api.get('/Enums/activation-methods'),

  // Get all available license statuses
  getLicenseStatuses: () => api.get('/Enums/license-statuses'),
}

export const featureFlagsApi = {
  // Core CRUD Operations

  // 1. Get all feature flags with pagination and filtering
  list: (params = {}) => api.get('/FeatureFlags', { params }),
  
  // 2. Create new feature flag
  create: (featureFlag) => api.post('/FeatureFlags', featureFlag),
  
  // 3. Get feature flag by ID
  get: (id) => api.get(`/FeatureFlags/${id}`),
  
  // 4. Update existing feature flag
  update: (id, featureFlag) => api.put(`/FeatureFlags/${id}`, featureFlag),
  
  // 5. Delete feature flag
  delete: (id) => api.delete(`/FeatureFlags/${id}`),
  
  // Bulk Operations
  
  // 6. Create feature flags for multiple users
  createBulk: (bulkData) => api.post('/FeatureFlags/bulk', bulkData),
  
  // 7. Create feature flags for all users in a building
  createForBuilding: (buildingData) => api.post('/FeatureFlags/building', buildingData),
  
  // User-specific Operations
  
  // 8. Check if a feature is enabled for a user
  checkUserFeature: (userId, params = {}) => api.get(`/FeatureFlags/check/${userId}`, { params }),
  
  // 9. Get all feature flags for a user
  getUserFeatures: (userId) => api.get(`/FeatureFlags/user/${userId}`),
  
  // 10. Get detailed feature flags for a user
  getUserFeatureDetails: (userId) => api.get(`/FeatureFlags/user/${userId}/details`),
  
  // 11. Remove all feature flags for a user
  deleteUserFeatures: (userId) => api.delete(`/FeatureFlags/user/${userId}`),
  
  // Building-specific Operations
  
  // 12. Get feature flags for a building
  getBuildingFeatures: (buildingId, params = {}) => api.get(`/FeatureFlags/building/${buildingId}`, { params }),
  
  // 13. Remove all feature flags for a building and feature type
  deleteBuildingFeatures: (buildingId, params = {}) => api.delete(`/FeatureFlags/building/${buildingId}`, { params }),
  
  // Statistics & Utilities
  
  // 14. Get feature flag statistics
  getStats: () => api.get('/FeatureFlags/stats'),
  
  // 15. Get all available feature flag types
  getTypes: () => api.get('/FeatureFlags/types'),
  
  // 16. Toggle feature flag status for a user
  toggle: (toggleData) => api.post('/FeatureFlags/toggle', toggleData),
}

export const licensePlanApi = {
  list: (params = {}) => api.get('/LicensePlans', { params }),
  get: (id) => api.get(`/LicensePlans/${id}`),
  create: (plan) => api.post('/LicensePlans', plan),
  update: (id, plan) => api.put(`/LicensePlans/${id}`, plan),
  delete: (id) => api.delete(`/LicensePlans/${id}`),
  setDefault: (id) => api.post(`/LicensePlans/${id}/set-default`),
}

export const buildingLicenseApi = {
  list: (params = {}) => api.get('/BuildingLicenses', { params }),
  getForBuilding: (buildingId) => api.get(`/BuildingLicenses/building/${buildingId}`),
  assign: (data) => api.post('/BuildingLicenses/assign', data),
  suspend: (id) => api.post(`/BuildingLicenses/${id}/suspend`),
  reactivate: (id) => api.post(`/BuildingLicenses/${id}/reactivate`),
  extend: (id, data) => api.post(`/BuildingLicenses/${id}/extend`, data),
  cancel: (id) => api.post(`/BuildingLicenses/${id}/cancel`),
}

export const licenseCodeApi = {
  list: (params = {}) => api.get('/LicenseCodes', { params }),
  create: (code) => api.post('/LicenseCodes', code),
  update: (id, code) => api.put(`/LicenseCodes/${id}`, code),
  delete: (id) => api.delete(`/LicenseCodes/${id}`),
}

export const licenseApi = {
  // Core License CRUD Operations

  // 1. Get all licenses with pagination and filtering
  list: (params = {}) => api.get('/Licenses', { params }),

  // 2. Create new license
  create: (license) => api.post('/Licenses', license),

  // 3. Get license by ID
  get: (id) => api.get(`/Licenses/${id}`),

  // 4. Update existing license
  update: (id, license) => api.put(`/Licenses/${id}`, license),

  // 5. Delete license
  delete: (id) => api.delete(`/Licenses/${id}`),

  // License Assignment Operations

  // 6. Assign license to user
  assign: (assignData) => api.post('/Licenses/assign', assignData),

  // User License Operations

  // 7. Get all user licenses with pagination and filtering
  getUserLicenses: (params = {}) => api.get('/Licenses/user-licenses', { params }),

  // 8. Get licenses for a specific user
  getForUser: (userId) => api.get(`/Licenses/user/${userId}`),
}

export const usersApi = {
  // Core User Operations

  // 1. Get all users with pagination and search
  list: (params = {}) => api.get('/Users', { params }),

  // 2. Get user by ID
  get: (userId) => api.get(`/Users/${userId}`),

  // 3. Create new user
  create: (user) => api.post('/Users', user),

  // 4. Update user status
  updateStatus: (userId, statusData) => api.put(`/Users/${userId}/status`, statusData),

  // 5. Reset user password
  resetPassword: (userId) => api.post(`/Users/${userId}/reset-password`),

  // 6. Get user roles
  getRoles: (userId) => api.get(`/Users/${userId}/roles`),

  // 7. Get user buildings
  getBuildings: (userId, params = {}) => api.get(`/Users/${userId}/buildings`, { params }),

  // 8. Get user statistics
  getStatistics: (userId) => api.get(`/Users/${userId}/statistics`),

  // 9. Get all available roles
  getAvailableRoles: () => api.get('/Users/roles'),
}

// Amenity APIs (admin endpoints)
export const amenityApi = {
  getPaged: (buildingId, pagedRequest = {}) =>
    api.get('/Amenity', { params: { buildingId, ...pagedRequest } }),
  get: (id) => api.get(`/Amenity/${id}`),
  create: (data) => api.post('/Amenity', data),
  update: (data) => api.put(`/Amenity/${data.amenityId || data.AmenityId}`, data),
  delete: (id) => api.delete(`/Amenity/${id}`),
  addUnavailableDate: (id, data) => api.post(`/Amenity/${id}/unavailable-dates`, data),
  updateUnavailableDate: (id, udId, data) => api.put(`/Amenity/${id}/unavailable-dates/${udId}`, data),
  deleteUnavailableDate: (id, udId) => api.delete(`/Amenity/${id}/unavailable-dates/${udId}`),
  uploadImage: (amenityId, formData) => api.post(`/Amenity/${amenityId}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
}

export const amenityBookApi = {
  getPaged: (buildingId, pagedRequest = {}) =>
    api.get('/AmenityBook', { params: { buildingId, ...pagedRequest } }),
  get: (id) => api.get(`/AmenityBook/${id}`),
  getByAmenityId: (amenityId) =>
    api.get(`/AmenityBook/by-amenity/${amenityId}`),
  create: (data) => api.post('/AmenityBook', data),
  confirm: (id) => api.post(`/AmenityBook/${id}/confirm`),
  decline: (id) => api.post(`/AmenityBook/${id}/decline`),
  delete: (id) => api.delete(`/AmenityBook/${id}`),
}

export const parkingSpotApi = {
  getPaged: (buildingId, pagedRequest = {}) =>
    api.get('/ParkingSpot', { params: { buildingId, ...pagedRequest } }),
  get: (id) => api.get(`/ParkingSpot/${id}`),
  create: (data) => api.post('/ParkingSpot', data),
  update: (data) => api.put(`/ParkingSpot/${data.parkingSpotId || data.ParkingSpotId}`, data),
  delete: (id) => api.delete(`/ParkingSpot/${id}`),
  batchCreate: (data) => api.post('/ParkingSpot/batch', data),
  addUnavailableDate: (id, data) => api.post(`/ParkingSpot/${id}/unavailable-dates`, data),
  updateUnavailableDate: (id, udId, data) => api.put(`/ParkingSpot/${id}/unavailable-dates/${udId}`, data),
  deleteUnavailableDate: (id, udId) => api.delete(`/ParkingSpot/${id}/unavailable-dates/${udId}`),
  uploadImage: (parkingSpotId, formData) => api.post(`/ParkingSpot/${parkingSpotId}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
}

export const parkingReservationApi = {
  getPaged: (buildingId, pagedRequest = {}) =>
    api.get('/ParkingReservation', { params: { buildingId, ...pagedRequest } }),
  get: (id) => api.get(`/ParkingReservation/${id}`),
  getByParkingSpotId: (parkingSpotId) => api.get(`/ParkingReservation/by-parking-spot/${parkingSpotId}`),
  create: (data) => api.post('/ParkingReservation', data),
  confirm: (id) => api.post(`/ParkingReservation/${id}/confirm`),
  decline: (id) => api.post(`/ParkingReservation/${id}/decline`),
  delete: (id) => api.delete(`/ParkingReservation/${id}`),
  checkIn: (data) => api.post('/ParkingReservation/check-in', data),
  checkOut: (id) => api.post(`/ParkingReservation/${id}/check-out`),
  getOccupancy: (buildingId) => api.get('/ParkingReservation/occupancy', { params: { buildingId } }),
}

export const parkingConfigurationApi = {
  get: (buildingId) => api.get('/ParkingConfiguration', { params: { buildingId } }),
  set: (data) => api.put('/ParkingConfiguration', data),
}

export const sentryApi = {
  getIssues: (params) => api.get('/Sentry/issues', { params }),
  getIssueDetail: (issueId) => api.get(`/Sentry/issues/${issueId}`),
  updateStatus: (issueId, status) => api.put(`/Sentry/issues/${issueId}/status`, { status }),
  getEnvironments: () => api.get('/Sentry/environments'),
  getIssueEvents: (issueId) => api.get(`/Sentry/issues/${issueId}/events`),
}

export const supportTaskApi = {
  list: (params) => api.get('/SupportTask', { params }),
  get: (id) => api.get(`/SupportTask/${id}`),
  create: (data) => api.post('/SupportTask', data),
  update: (id, data) => api.put(`/SupportTask/${id}`, data),
  delete: (id) => api.delete(`/SupportTask/${id}`),
  updateStatus: (id, data) => api.put(`/SupportTask/${id}/status`, data),
  addComment: (id, data) => api.post(`/SupportTask/${id}/comments`, data),
  deleteComment: (commentId) => api.delete(`/SupportTask/comments/${commentId}`),
  uploadAttachment: (id, formData) => api.post(`/SupportTask/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (attachmentId) => api.delete(`/SupportTask/attachments/${attachmentId}`),
  uploadTempImage: (formData) => api.post('/SupportTask/upload-temp-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getKanban: (params) => api.get('/SupportTask/kanban', { params }),
  generatePublicLink: (id) => api.post(`/SupportTask/${id}/share`),
  getMembers: () => api.get('/SupportTask/members'),
  createMember: (data) => api.post('/SupportTask/members', data),
  updateMember: (id, data) => api.put(`/SupportTask/members/${id}`, data),
  deleteMember: (id) => api.delete(`/SupportTask/members/${id}`),
}

export const deviceInstallationApi = {
  list: (params = {}) => api.get('/DeviceInstallations', { params }),
  getStats: () => api.get('/DeviceInstallations/stats'),
  getByUser: (userId) => api.get(`/DeviceInstallations/user/${userId}`),
  delete: (id) => api.delete(`/DeviceInstallations/${id}`),
  deleteByUser: (userId) => api.delete(`/DeviceInstallations/user/${userId}`),
  getAzureRegistrations: (tag) => api.get('/DeviceInstallations/azure-registrations', { params: { tag } }),
  deleteAzureRegistration: (registrationId) => api.delete(`/DeviceInstallations/azure-registrations/${registrationId}`),
  getMatchedRegistrations: (environment) => api.get('/DeviceInstallations/matched', { params: { environment } }),
  sendTestNotification: (userId, message) => api.post('/DeviceInstallations/send-test', { userId, message }),
}

export const knowledgeBaseApi = {
  // Documents
  getDocuments: (params = {}) => api.get('/KnowledgeBase/documents', { params }),
  getDocument: (id) => api.get(`/KnowledgeBase/documents/${id}`),
  uploadDocument: (formData) => api.post('/KnowledgeBase/documents/upload', formData, { headers: { 'Content-Type': undefined } }),
  analyzeDocument: (formData) => api.post('/KnowledgeBase/documents/analyze', formData, { headers: { 'Content-Type': undefined }, timeout: 120000 }),
  batchAnalyzeDocuments: (files) => api.post('/KnowledgeBase/documents/analyze-batch', { files }, { timeout: 120000 }),
  updateDocument: (id, data) => api.put(`/KnowledgeBase/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/KnowledgeBase/documents/${id}`),
  deleteMultipleDocuments: (ids) => api.post('/KnowledgeBase/documents/delete-multiple', ids),

  // Topics
  getTopics: (buildingId) => api.get('/KnowledgeBase/topics', { params: { buildingId } }),
  createTopic: (data) => api.post('/KnowledgeBase/topics', data),
  updateTopic: (id, data) => api.put(`/KnowledgeBase/topics/${id}`, data),
  deleteTopic: (id) => api.delete(`/KnowledgeBase/topics/${id}`),
  generateTopics: (buildingId) => api.post('/KnowledgeBase/topics/generate', null, { params: { buildingId }, timeout: 120000 }),
  assignDocumentToTopic: (docId, topicId) => api.post(`/KnowledgeBase/documents/${docId}/topics/${topicId}`),
  removeDocumentFromTopic: (docId, topicId) => api.delete(`/KnowledgeBase/documents/${docId}/topics/${topicId}`),
  autoAssignDocuments: (buildingId) => api.post('/KnowledgeBase/topics/auto-assign', null, { params: { buildingId }, timeout: 120000 }),

  // Sync
  triggerSync: () => api.post('/KnowledgeBase/sync'),
  getSyncStatus: (ingestionJobId) => api.get('/KnowledgeBase/sync/status', { params: { ingestionJobId } }),

  // Stats
  getStats: (buildingId) => api.get('/KnowledgeBase/stats', { params: { buildingId } }),

  // Sessions / Chat
  getSessions: (params = {}) => api.get('/KnowledgeBase/sessions', { params }),
  getSession: (id) => api.get(`/KnowledgeBase/sessions/${id}`),
  chat: (data) => api.post('/KnowledgeBase/chat', data, { timeout: 120000 }),
  deleteSession: (id) => api.delete(`/KnowledgeBase/sessions/${id}`),

  // Presigned URLs
  getPresignedUrl: (s3Key) => api.get('/KnowledgeBase/documents/presigned-url', { params: { s3Key } }),
}

export const adminAuthApi = {
  me: () => authApiInstance.get('/auth/me'),
  myBuildings: () => authApiInstance.get('/auth/my-buildings'),
}

export const notificationEventsApi = {
  list: () => api.get('/notification-events'),
  get: (eventType) => api.get(`/notification-events/${eventType}`),
  updateChannels: (eventType, data) => api.put(`/notification-events/${eventType}/channels`, data),
  getCategories: () => api.get('/notification-events/categories'),
}

export const pushTemplatesApi = {
  getTypes: () => api.get('/push-templates/types'),
  getVariables: () => api.get('/push-templates/variables'),
  getVariablesByType: (templateType) => api.get(`/push-templates/variables/${templateType}`),
  list: () => api.get('/push-templates/list'),
  create: (template) => api.post('/push-templates/create', template),
  get: (templateType) => api.get(`/push-templates/get/${templateType}`),
  update: (template) => api.put('/push-templates/update', template),
  delete: (templateType) => api.delete(`/push-templates/delete/${templateType}`),
  send: (request) => api.post('/push-templates/send', request),
  generateAI: (aiRequest) => api.post('/push-templates/ai/generate', aiRequest),
}

// EmailTemplateType is now fetched dynamically from the API

export default api